import React, { useRef, useState, useEffect } from 'react';
import request from 'ca-request';
import ValidationError from '../ValidationError/ValidationError';
import notifySavingError from '../helpers/notifySavingError';
import { CommentableSelectOption, CheckedOption } from '../types';
import styles from './CommentableSelectQuestion.scss';
import CommentableSelectOptions from './CommentableSelectOptions/CommentableSelectOptions';
import Submission from '../Submission/Submission';
import { CommentableSelectQuestionContainer } from 'ca-capture/CommentableSelectQuestion/CommentableSelectQuestionContainer';

type Props = {
  questionId: string;
  questionText: string;
  questionMandatory: boolean;
  options: CommentableSelectOption[];
  selectedOptionIds: string[];
  selectionLimit: number;
  requestURL: string;
  uiText: {
    selectCount: string;
    selectionLimitError: string;
    savingError: string;
    otherLabel: string;
    regularLabel: string;
    mandatoryError: string;
    forLabel: string;
  };
};

const CommentableSelectQuestion = (props: Props) => {
  const {
    questionId,
    questionText,
    questionMandatory,
    selectionLimit,
    options,
    uiText,
  } = props;
  const [validationError, setValidationError] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const questionHeaderId = `question-header-${questionId}`;
  const questionLimitId = `question-limit-${questionId}`;
  const questionLabel = questionMandatory ? `${questionText} *` : questionText;
  const validationErrorId = `${questionId}-error`;

  // Without having a React container around the Capture form yet, we're listening for a custom event
  // sent from app/assets/javascripts/response.js to signal that submit has been pressed and we want to show validation errors.
  useEffect(() => {
    const checkAndSetError = (_event: any, questionIds: string[]) => {
      const answered = questionIds.indexOf(questionId) === -1;
      setValidationError(!answered);
    };
    if (questionMandatory) {
      $(document).on('promptUnanswered', checkAndSetError);
    }
    return () => {
      $(document).off('promptUnanswered', checkAndSetError);
    };
  }, [questionId, questionMandatory]);

  const saveCheck = (optionList: CheckedOption[], otherComment?: string) => {
    // Shape the data for the request
    const optionObject = Object.assign(
      {},
      ...optionList.map((o) => ({ [o.id]: o.checked }))
    );

    const requestData =
      otherComment != null
        ? {
            ...optionObject,
            text_value: otherComment,
          }
        : optionObject;

    Submission.enqueue(() => {
      return request
        .put(props.requestURL, {
          body: JSON.stringify(requestData),
        })
        .catch((error) => {
          notifySavingError(
            error,
            props.requestURL,
            'commentable select option request',
            uiText.savingError
          );
        });
    });

    // Send back to jQuery for some adjustments to the .question container that we can't make here yet
    const hasAtLeastOneChecked = optionList.find((o) => o.checked === true);
    $(document).trigger('reactAnswerChange', {
      container: containerRef.current,
      answered: hasAtLeastOneChecked,
    });
  };

  const saveComment = (path: string, comment: string) => {
    Submission.enqueue(() => {
      return request
        .put(path, {
          body: JSON.stringify({
            comment: comment,
          }),
        })
        .catch((error) => {
          notifySavingError(
            error,
            path,
            'commentable select comment save request',
            uiText.savingError
          );
        });
    });
  };

  const deleteComment = (path: string) => {
    Submission.enqueue(() => {
      return request.delete(path).catch((error) => {
        notifySavingError(
          error,
          path,
          'commentable select comment delete request',
          uiText.savingError
        );
      });
    });
  };

  return (
    <CommentableSelectQuestionContainer ref={containerRef}>
      <div className={styles.questionHeader} id={questionHeaderId}>
        {questionLabel}
      </div>
      <div>
        {selectionLimit > 0 && (
          <div id={questionLimitId} className={styles.limitInfo}>
            {uiText.selectCount}
          </div>
        )}
      </div>
      <div
        role="group"
        aria-labelledby={questionHeaderId}
        aria-describedby={
          [
            validationError && validationErrorId,
            selectionLimit > 0 && questionLimitId,
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
        data-focus-to-on-navigate="true"
        tabIndex={-1}
        className={styles.inputGroup}
      >
        <CommentableSelectOptions
          questionId={questionId}
          options={options}
          selectedOptionIds={props.selectedOptionIds}
          selectionLimit={selectionLimit}
          uiText={uiText}
          saveCheck={saveCheck}
          deleteComment={deleteComment}
          saveComment={saveComment}
        />
      </div>
      {validationError && (
        <ValidationError
          id={validationErrorId}
          uiText={{ message: uiText.mandatoryError }}
        />
      )}
    </CommentableSelectQuestionContainer>
  );
};

export default CommentableSelectQuestion;
